import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useUserSingleAutoStack } from "../../hooks/query/queries";
import {
  Typography,
  FormField,
  FormItem,
  FormMessage,
  CustomFrequencyDropdown,
  StackAmountInput,
  FormSection,
  PauseStackModal,
} from "../../components/ui";
import StackFormLayout from "../../components/ui/stack-form-layout";
import StackFormButtons from "../../components/ui/stack-form-buttons";
import AutoStackStatusStep from "../../components/AutoStackStatusStep";
import { VALIDATION_MESSAGES } from "../../constants/autostack";
import { updatePatch } from "../../services/apiService";
import { updateUserAutoStack } from "../../services/autostackService";
import { UserAutoStackResponse } from "../../types/generated/api";
import { localTimeToUTC, utcTimeToLocal } from "../../utils/dateTime";

const editAutoStackFormSchema = z.object({
  stackName: z
    .string()
    .transform((val) => val.trim())
    .refine((val) => val.length > 0, {
      message: VALIDATION_MESSAGES.REQUIRED_STACK_NAME,
    })
    .refine((val) => val.length <= 50, {
      message: VALIDATION_MESSAGES.STACK_NAME_MAX_LENGTH,
    }),
  stackAmount: z
    .string()
    .min(1, VALIDATION_MESSAGES.REQUIRED_AMOUNT)
    .refine((val) => {
      const num = Number(val.replace(/,/g, ""));
      return !isNaN(num) && num > 0;
    }, VALIDATION_MESSAGES.INVALID_AMOUNT_MIN),
  frequency: z
    .object({
      type: z.enum(["day", "week", "month"]),
      dayOfWeek: z.string().optional(),
      dayOfMonth: z.string().optional(),
      time: z.string().optional(),
    })
    .refine((data) => data.type, VALIDATION_MESSAGES.REQUIRED_FREQUENCY)
    .refine((data) => {
      if (data.type === "day" && !data.time) {
        return false;
      }
      return true;
    }, VALIDATION_MESSAGES.REQUIRED_TIME),
});

export type EditAutoStackFormData = z.infer<typeof editAutoStackFormSchema>;

const transformApiDataToForm = (apiData: any): EditAutoStackFormData => {
  const frequencyMap = {
    daily: "day",
    weekly: "week",
    monthly: "month",
  } as const;

  const formFrequencyType =
    frequencyMap[apiData.frequency as keyof typeof frequencyMap] || "week";

  let frequencyObj: EditAutoStackFormData["frequency"] = {
    type: formFrequencyType,
  };

  if (apiData.schedule) {
    if (
      apiData.schedule.dayOfWeek &&
      typeof apiData.schedule.dayOfWeek === "string"
    ) {
      frequencyObj.dayOfWeek = apiData.schedule.dayOfWeek;
    }
    if (apiData.schedule.time && typeof apiData.schedule.time === "string") {
      frequencyObj.time = utcTimeToLocal(apiData.schedule.time);
    }
    if (
      apiData.schedule.monthlyType &&
      typeof apiData.schedule.monthlyType === "string"
    ) {
      if (
        apiData.schedule.monthlyType === "custom" &&
        apiData.schedule.customDay &&
        typeof apiData.schedule.customDay === "number"
      ) {
        frequencyObj.dayOfMonth = apiData.schedule.customDay.toString();
      } else {
        frequencyObj.dayOfMonth = apiData.schedule.monthlyType;
      }
    }
  }

  return {
    stackName: apiData.name || "",
    stackAmount: apiData.amount?.toString() || "",
    frequency: frequencyObj,
  };
};

const transformFormDataToApi = (formData: EditAutoStackFormData) => {
  const { stackName, stackAmount, frequency } = formData;

  const frequencyMap = {
    day: "daily",
    week: "weekly",
    month: "monthly",
  } as const;

  const apiFrequency = frequencyMap[frequency.type];

  let schedule: any = {};
  // Default time if not specified (matches TIME_SLOTS[0])
  const defaultTime = frequency.time ?? "08:00";

  if (frequency.type === "day") {
    schedule = { time: localTimeToUTC(defaultTime) };
  } else if (frequency.type === "week") {
    schedule = {
      dayOfWeek: (frequency.dayOfWeek ?? "tuesday").toLowerCase(),
      time: localTimeToUTC(defaultTime),
    };
  } else if (frequency.type === "month") {
    if (frequency.dayOfMonth === "first" || frequency.dayOfMonth === "last") {
      schedule = {
        monthlyType: frequency.dayOfMonth,
        time: localTimeToUTC(defaultTime),
      };
    } else if (frequency.dayOfMonth && !isNaN(parseInt(frequency.dayOfMonth))) {
      schedule = {
        monthlyType: "custom",
        customDay: parseInt(frequency.dayOfMonth),
        time: localTimeToUTC(defaultTime),
      };
    } else {
      schedule = {
        monthlyType: "first",
        time: localTimeToUTC(defaultTime),
      };
    }
  }

  const normalizedAmount = String(stackAmount || "")
    .replace(/,/g, "")
    .replace(/\s/g, "");
  const amount = parseFloat(normalizedAmount);

  if (Number.isNaN(amount) || amount <= 0) {
    throw new Error(VALIDATION_MESSAGES.INVALID_AMOUNT_MIN);
  }

  return {
    amount,
    frequency: apiFrequency,
    schedule,
    name: stackName,
  };
};

export default function EditAutoStack() {
  const { stackId } = useParams<{ stackId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    data: userSingleAutoStack,
    isLoading: isLoadingUserSingleAutoStack,
    isFetching: isFetchingUserSingleAutoStack,
  } = useUserSingleAutoStack(stackId);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [updateStatus, setUpdateStatus] = React.useState<
    null | "success" | "error"
  >(null);
  const [isPauseDialogOpen, setIsPauseDialogOpen] = React.useState(false);
  const [isPausing, setIsPausing] = React.useState(false);

  const form = useForm<EditAutoStackFormData>({
    resolver: zodResolver(editAutoStackFormSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (userSingleAutoStack) {
      if (!form.getValues("stackAmount")) {
        const formData = transformApiDataToForm(userSingleAutoStack);
        form.reset(formData);
      }
    }
  }, [userSingleAutoStack, form]);

  const handleUpdate = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    setUpdateStatus(null);
    setIsUpdating(true);
    try {
      const formData = form.getValues();
      const apiData = transformFormDataToApi(formData);

      const response = await updatePatch(
        `/api/v1/auto-stacking/auto-stacks/${stackId}`,
        {},
        apiData
      );

      if (response.status === 200 || response.status === 204) {
        setUpdateStatus("success");
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["userAutoStacks"] }),
          // Critical: refresh single-stack cache so detail view shows new amount/schedule immediately
          queryClient.invalidateQueries({
            queryKey: ["userSingleAutoStack", stackId],
          }),
          queryClient.invalidateQueries({ queryKey: ["autoStackStats"] }),
          queryClient.invalidateQueries({ queryKey: ["currencies"] }),
          queryClient.invalidateQueries({ queryKey: ["notifications"] }),
        ]);
      } else {
        throw new Error("Failed to update AutoStack");
      }
    } catch (error) {
      console.error("Error updating AutoStack:", error);
      setUpdateStatus("error");
    } finally {
      setIsUpdating(false);
    }
  }, [form, queryClient, stackId]);

  const handleBack = useCallback(() => {
    navigate(`/autostack/${stackId}`);
  }, [navigate, stackId]);

  const handleTogglePause = useCallback(async () => {
    if (!stackId || !userSingleAutoStack) return;

    const newStatus =
      userSingleAutoStack.status === "active" ? "paused" : "active";
    setIsPausing(true);

    try {
      queryClient.setQueryData(
        ["userSingleAutoStack", stackId],
        (oldData: UserAutoStackResponse) => {
          return { ...oldData, status: newStatus };
        }
      );

      await updateUserAutoStack(stackId, {
        status: newStatus,
      });

      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["userSingleAutoStack"] }),
        queryClient.refetchQueries({ queryKey: ["userAutoStacks"] }),
        queryClient.refetchQueries({ queryKey: ["autoStackStats"] }),
      ]);
    } catch (error) {
      console.error("Failed to update stack status:", error);
      queryClient.setQueryData(
        ["userSingleAutoStack", stackId],
        (oldData: UserAutoStackResponse) => {
          if (!oldData) return oldData;
          return { ...oldData, status: userSingleAutoStack.status };
        }
      );
    } finally {
      setIsPausing(false);
    }
  }, [stackId, userSingleAutoStack, queryClient]);

  const handlePauseClick = useCallback(() => {
    if (userSingleAutoStack?.status === "active") {
      setIsPauseDialogOpen(true);
    } else {
      void handleTogglePause();
    }
  }, [userSingleAutoStack?.status, handleTogglePause]);

  const handleStatusStepContinue = useCallback(() => {
    if (updateStatus === "success") {
      navigate("/autostack");
    } else {
      setUpdateStatus(null);
    }
  }, [updateStatus, navigate]);

  if (isLoadingUserSingleAutoStack || isFetchingUserSingleAutoStack) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-20 h-20 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!userSingleAutoStack) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Typography variant="p">AutoStack not found</Typography>
      </div>
    );
  }

  if (updateStatus !== null) {
    return (
      <AutoStackStatusStep
        isSuccess={updateStatus === "success"}
        onContinue={handleStatusStepContinue}
        editStack
      />
    );
  }

  return (
    <FormProvider {...form}>
      <StackFormLayout
        title={`Edit ${userSingleAutoStack.name || "AutoStack"}`}
        onCancel={handlePauseClick}
        showCancelHeader={true}
        maxWidth="max-w-3xl"
        cancelButtonText={
          isPausing ||
          isLoadingUserSingleAutoStack ||
          isFetchingUserSingleAutoStack
            ? "Loading..."
            : userSingleAutoStack?.status === "active"
              ? "Pause Stack"
              : "Resume Stack"
        }
        cancelButtonDisabled={
          isPausing ||
          isLoadingUserSingleAutoStack ||
          isFetchingUserSingleAutoStack
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdate();
          }}
          className="space-y-4"
        >
          <FormSection className="flex flex-col gap-2 bg-transparent border-none !p-0">
            <Typography variant="label" className="text-text-secondary">
              Name
            </Typography>
            <FormField
              control={form.control}
              name="stackName"
              render={({ field }) => (
                <FormItem className="w-full">
                  <input
                    {...field}
                    type="text"
                    placeholder="My BTC Vault"
                    className="w-full h-14 px-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>

          <FormSection className="flex flex-col justify-between gap-3">
            <Typography variant="label">I want to stack</Typography>
            <div className="flex justify-start md:justify-end">
              <StackAmountInput
                name="stackAmount"
                className="w-full md:w-1/3"
                label="Stack amount"
              />
            </div>
          </FormSection>

          <FormSection className="flex flex-col gap-2">
            <Typography variant="label">Every</Typography>
            <div className="w-full md:w-1/3 flex">
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <CustomFrequencyDropdown
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select frequency"
                      className="w-full"
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>

          <StackFormButtons
            onBack={handleBack}
            continueText={isUpdating ? "Saving..." : "Save"}
            isSubmitType={true}
            disabled={isPausing}
            loading={isUpdating}
          />
        </form>
      </StackFormLayout>

      <PauseStackModal
        isOpen={isPauseDialogOpen}
        setIsPauseDialogOpen={setIsPauseDialogOpen}
        onConfirmPause={() => {
          void handleTogglePause();
        }}
      />
    </FormProvider>
  );
}
